/**
 * AcDbAssocArray writer — Batch-8 S-ARRAYASSOC-WRITE (L4).
 *
 * Reverses `collectAssocArrays` / `decodeAssocArrayObject` in
 * `assocArrayParser.ts`. Emits one OBJECTS record per `DxfAssocArrayObject`.
 *
 * Round-trip contract (mirrors `serializeDynamicBlockObject` /
 * `serializeAssocVariable`):
 *   - When `obj.rawGroups` is populated by the parser we re-emit it verbatim
 *     so EVERY group the original file carried — the AcDbAssocAction status
 *     words, GUID payloads, 360/350 cross-links, item-data sub-records, and
 *     the POLAR/PATH parameter records we decode only opaquely — survives
 *     byte-for-byte. This is the authoritative round-trip channel; the decoded
 *     `rectParams` / `dependencyRefHandle` are an auxiliary projection for the
 *     resolver + model.
 *   - When `obj.rawGroups` is empty (synthetic construction — e.g. a future
 *     bridge that authors an array from scratch) we rebuild the minimal
 *     canonical shape from the decoded fields. The rectangular-parameters
 *     record re-encodes the named int/double parameters in the canonical
 *     order documented in `assocArrayParser.ts`.
 */
import type {
  AssocArrayRectParams,
  DxfAssocArrayObject,
} from "./assocArrayParser";
import {
  ASSOC_ARRAY_PLACEHOLDER_CODE,
  ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX,
} from "./assocArrayPlaceholder";
import type { DxfGroup } from "./types";

export interface AssocArrayPlaceholderResult {
  readonly object: DxfAssocArrayObject | undefined;
  readonly groups: DxfGroup[];
}

/**
 * Serialise one `DxfAssocArrayObject` into the flat DxfGroup stream the
 * OBJECTS-section writer appends to its output.
 */
export function serializeAssocArrayObject(
  obj: DxfAssocArrayObject,
): DxfGroup[] {
  if (obj.rawGroups.length > 0) {
    // Verbatim round-trip — the parser preserved the full source stream.
    return obj.rawGroups.slice();
  }

  // Synthetic construction fallback (no source stream). Build the minimal
  // canonical shape from the decoded fields.
  const out: DxfGroup[] = [];
  out.push({ code: 0, value: obj.objectType });
  if (obj.handle) out.push({ code: 5, value: obj.handle });
  if (obj.ownerHandle) out.push({ code: 330, value: obj.ownerHandle });
  for (const marker of obj.subclassMarkers) {
    out.push({ code: 100, value: marker });
  }
  if (obj.paramFamily === "rectangular" && obj.rectParams) {
    out.push({ code: 100, value: "AcDbAssocArrayRectangularParameters" });
    appendRectParams(out, obj.rectParams);
  }
  if (obj.dependencyRefHandle) {
    out.push({ code: 330, value: obj.dependencyRefHandle });
  }
  return out;
}

/** Resolve and serialize an OBJECTS assoc-array sentinel at its source position. */
export function serializeAssocArrayPlaceholder(
  group: DxfGroup,
  objects: readonly DxfAssocArrayObject[] | undefined,
  byHandle: ReadonlyMap<string, DxfAssocArrayObject>,
): AssocArrayPlaceholderResult | undefined {
  if (group.code !== ASSOC_ARRAY_PLACEHOLDER_CODE) return undefined;
  const object = group.value.startsWith(ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX)
    ? objects?.[
        Number(group.value.slice(ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX.length))
      ]
    : byHandle.get(group.value);
  return {
    object,
    groups: object ? serializeAssocArrayObject(object) : [],
  };
}

/**
 * Re-encode the rectangular parameters in the canonical order documented in
 * `assocArrayParser.ts`:
 *   group-90 ints  : [columnCount, rowCount, levelCount]
 *   group-40 reals : [columnSpacing, rowSpacing, levelSpacing, axisAngle,
 *                     rowElevation]
 *
 * When the positional `intParams` / `doubleParams` arrays were preserved we
 * emit those (they carry exactly what the producer wrote, including counts we
 * could not name). Otherwise we synthesise from the named fields.
 */
function appendRectParams(out: DxfGroup[], p: AssocArrayRectParams): void {
  const ints = p.intParams.length
    ? p.intParams
    : [p.columnCount, p.rowCount, p.levelCount].filter(
        (n): n is number => n !== undefined,
      );
  const doubles = p.doubleParams.length
    ? p.doubleParams
    : [
        p.columnSpacing,
        p.rowSpacing,
        p.levelSpacing,
        p.axisAngle,
        p.rowElevation,
      ].filter((n): n is number => n !== undefined);
  for (const n of ints) out.push({ code: 90, value: String(n) });
  for (const n of doubles) out.push({ code: 40, value: String(n) });
  for (const e of p.expressions) out.push({ code: 1, value: e });
}
